import { createApi } from "@reduxjs/toolkit/query/react";
import axiosBaseQuery from "../../config/service";
import CryptoJS from "crypto-js";
import { HMAC_SECRET_KEY } from "../../config/api.config";

export const userApiService = createApi({
  reducerPath: "userApiService",
  baseQuery: axiosBaseQuery(),
  endpoints: (builder) => ({
    fetchUserOrders: builder.mutation({
      query: ({
        pageSize,
        pageIndex,
        filter = {},
        sortColumn,
        sortDirection,
      }) => {
        const token = JSON.parse(localStorage.getItem("token"));
        return {
          url: `/api/payment/v1/customer/order/overview?pageSize=${pageSize}&pageIndex=${pageIndex}`,
          data: { filter, sortColumn, sortDirection },
          method: "POST",
          headers: { "x-auth-token": token },
        };
      },
      invalidatesTags: ["fetchUserOrders"],
    }),
    getPaymentLinkByOrderId: builder.query({
      query: (id) => {
        const token = JSON.parse(localStorage.getItem("token"));
        return {
          url: `/api/payment/v1/customer/order/payment-link/${id}`,
          method: "GET",
          headers: { "x-auth-token": token },
        };
      },
      providesTags: ["getPaymentLinkByOrderId"],
    }),
    // Full single-order detail (input fields, documents, SLA, property) for the
    // order-details drawer. The overview list row is a curated summary and does
    // NOT carry `orderInfo`, so the drawer must fetch the order by its `orderId`.
    // Same live endpoint the mobile app uses (GET /api/payment/order/{orderId}).
    fetchUserOrderId: builder.query({
      query: (id) => {
        const token = JSON.parse(localStorage.getItem("token"));
        return {
          url: `/api/payment/order/${id}`,
          method: "GET",
          headers: { "x-auth-token": token },
        };
      },
      providesTags: ["fetchUserOrders"],
    }),
    fetchUserTableCount: builder.query({
      query: (id) => {
        const token = JSON.parse(localStorage.getItem("token"));
        return {
          url: `/api/payment/v1/customer/${id}/overview/count`,
          method: "GET",
          headers: { "x-auth-token": token },
        };
      },
      providesTags: ["fetchUserTableCount"],
    }),
    fetchCreditHistory: builder.mutation({
      query: ({
        pageSize,
        pageIndex,
        filter = {},
        sortColumn,
        sortDirection,
      }) => {
        const token = JSON.parse(localStorage.getItem("token"));
        return {
          url: `/api/payment/v1/customer/credit/history?pageSize=${pageSize}&pageIndex=${pageIndex}`,
          data: { filter, sortColumn, sortDirection },
          method: "POST",
          headers: { "x-auth-token": token },
        };
      },
      invalidatesTags: ["fetchCreditHistory"],
    }),
    fetchUserTransactions: builder.mutation({
      query: ({
        pageSize,
        pageIndex,
        filter = {},
        sortColumn,
        sortDirection,
      }) => {
        const token = JSON.parse(localStorage.getItem("token"));
        // Sanitise pagination: callers sometimes pass null/undefined which
        // serialises into the URL as the literal string 'null' and breaks
        // the backend filter (the user-spotted bug). Coerce to safe ints.
        const ps = Number.isFinite(Number(pageSize)) && Number(pageSize) > 0 ? Number(pageSize) : 15;
        const pi = Number.isFinite(Number(pageIndex)) && Number(pageIndex) > 0 ? Number(pageIndex) : 1;
        return {
          url: `/api/payment/v1/customer/transaction/overview?pageSize=${ps}&pageIndex=${pi}`,
          data: { filter, sortColumn, sortDirection },
          method: "POST",
          headers: { "x-auth-token": token },
        };
      },
      invalidatesTags: ["fetchCreditHistory"],
    }),
    fetchLawyerCasesOverview: builder.query({
      query: ({
        pageSize = 15,
        pageIndex = 1,
        filter = {},
        sortColumn,
        sortDirection,
      }) => {
        const token = JSON.parse(localStorage.getItem("token"));
        return {
          url: `/api/payment/v1/app/order/lawyer-orders/overview?pageIndex=${pageIndex}&pageSize=${pageSize}`,
          data: { filter, sortColumn, sortDirection },
          method: "POST",
          headers: { "x-auth-token": token },
        };
      },
      providesTags: ["LawyerCases"],
    }),
    fetchLawyerCaseById: builder.query({
      query: (id) => {
        const token = JSON.parse(localStorage.getItem("token"));
        return {
          url: `/api/payment/v1/app/order/lawyer-orders/${id}`,
          method: "GET",
          headers: { "x-auth-token": token },
        };
      },
      providesTags: (result, error, id) => [{ type: "LawyerCases", id }],
    }),

    //     uploadLawyerDocument: builder.mutation({
    //   query: ({ docId, orderId, file, docType }) => {
    //     const token = JSON.parse(localStorage.getItem("token"));
    //     const formData = new FormData();
    //     formData.append("files", file);
    //     const type = docType || docId;
    //     return {
    //       url: `/api/payment/v1/lawyersp/upload/${docId}/${orderId}/${type}`,
    //       method: "PUT",
    //       data: formData,
    //       headers: {
    //         "x-auth-token": token,
    //         "Content-Type": "multipart/form-data",
    //       },
    //     };
    //   },
    // }),
    uploadLawyerCaseDocument: builder.mutation({
      query: ({ docId, orderId, docType, file }) => {
        const token = JSON.parse(localStorage.getItem("token"));
        const formData = new FormData();
        formData.append("files", file);

        return {
          url: `/api/payment/v1/app/order/lawyer-orders/upload/${docId}/${orderId}/${docType}`,
          method: "PUT",
          data: formData,
          headers: {
            "x-auth-token": token,
            "Content-Type": "multipart/form-data",
          },
        };
      },
      invalidatesTags: (result, error, { orderId }) => [
        { type: "LawyerCases", id: orderId },
      ],
    }),
    createOrganization: builder.mutation({
      query: (data) => {
        const token = JSON.parse(localStorage.getItem("token"));
        return {
          url: "/jaaga/api/user/organization/app/createOrganization",
          data: data,
          method: "POST",
          headers: { "x-auth-token": token },
        };
      },
      invalidatesTags: ["Organizations"],
    }),
    deleteOrganization: builder.mutation({
      query: (id) => {
        const token = JSON.parse(localStorage.getItem("token"));
        return {
          url: `/jaaga/api/user/organization/app/deleteOrganization?id=${id}`,
          method: "DELETE",
          headers: { "x-auth-token": token },
        };
      },
      invalidatesTags: ["Organizations"],
    }),
    updateOrganization: builder.mutation({
      query: ({ id, data }) => {
        // console.log(data);
        const token = JSON.parse(localStorage.getItem("token"));
        return {
          url: `/jaaga/api/user/organization/app/updateOrganization?id=${id}`,
          method: "PUT",
          data: data,
          headers: { "x-auth-token": token },
        };
      },
      invalidatesTags: ["Organizations"],
    }),
    uploadOrgTemplate: builder.mutation({
      query: ({ bank, product, id, file }) => {
        const token = JSON.parse(localStorage.getItem("token"));

        const formData = new FormData();
        formData.append("template", file);
        return {
          url: `/jaaga/api/user/organization/app/lawyer/template/${bank}/${product}?id=${id}`,
          method: "PUT",
          data: formData,
          headers: {
            "x-auth-token": token,
            "Content-Type": "multipart/form-data",
          },
        };
      },
    }),
    uploadOrganizationLogo: builder.mutation({
      query: ({ id, file }) => {
        const token = JSON.parse(localStorage.getItem("token"));
        const formData = new FormData();
        formData.append("logos", file);
        return {
          url: `/jaaga/api/user/organization/app/lawyer/uploadOrganizationLogo?id=${id}`,
          method: "PUT",
          data: formData,
          headers: {
            "x-auth-token": token,
            "Content-Type": "multipart/form-data",
          },
        };
      },
      invalidatesTags: ["getOneOrganizations"],
    }),
    updateOrgDocuments: builder.mutation({
      query: ({ bank, product, id, documents, checklist }) => {
        const token = JSON.parse(localStorage.getItem("token"));
        return {
          url: `/jaaga/api/user/organization/app/lawyer/documents/${bank}/${product}?id=${id}`,
          method: "PUT",
          data: { documents, checklist },
          headers: { "x-auth-token": token },
        };
      },
    }),
    fetchListOfPartnerOrganisations: builder.mutation({
      query: ({
        pageSize,
        pageIndex,
        filter = {},
        sortColumn,
        sortDirection,
      }) => {
        const token = JSON.parse(localStorage.getItem("token"));
        // ?pageSize=${pageSize}&pageIndex=${pageIndex}.
        return {
          url: `/jaaga/api/user/sp/organization/list`,
          data: { filter, sortColumn, sortDirection },
          method: "POST",
          headers: { "x-auth-token": token },
        };
      },
      invalidatesTags: ["fetchListOfPartnerOrganisations"],
    }),
    fetchListOfOrganisations: builder.mutation({
      query: ({
        pageSize,
        pageIndex,
        filter = {},
        sortColumn,
        sortDirection,
      }) => {
        const token = JSON.parse(localStorage.getItem("token"));
        return {
          url: `/jaaga/api/user/organization/app/listOrganizations?pageSize=${pageSize}&pageIndex=${pageIndex}`,
          data: { filter, sortColumn, sortDirection },
          method: "POST",
          headers: { "x-auth-token": token },
        };
      },
      invalidatesTags: ["Organizations"],
    }),
    fetchAdminListOfOrganisations: builder.mutation({
      query: ({ orderId, filter }) => {
        const token = JSON.parse(localStorage.getItem("token"));
        return {
          url: `/api/payment/v1/app/order/${orderId}/assignment/organizations`,
          data: { filter },
          method: "POST",
          headers: { "x-auth-token": token },
        };
      },
      invalidatesTags: ["adminOrganizations"],
    }),
    getOneOrganizations: builder.query({
      query: (id) => {
        const token = JSON.parse(localStorage.getItem("token"));

        return {
          url: `/jaaga/api/user/organization/app/getOrganization?id=${id}`,
          method: "GET",
          headers: { "x-auth-token": token },
        };
      },
      providesTags: ["getOneOrganizations"],
    }),
    getPartnerOrganization: builder.query({
      query: (id) => {
        const token = JSON.parse(localStorage.getItem("token"));
        return {
          url: `/jaaga/api/user/sp/organization/getOrganization?id=${id}`,
          method: "GET",
          headers: { "x-auth-token": token },
        };
      },
      providesTags: ["getPartnerOrganization"],
    }),
    assignAdminOrganizations: builder.mutation({
      query: (data) => {
        const token = JSON.parse(localStorage.getItem("token"));

        return {
          url: `/jaaga/api/user/organization/app/assignAdmin`,
          data: data,
          method: "POST",
          headers: { "x-auth-token": token },
        };
      },
      invalidatesTags: ["assignAdminOrganizations"],
    }),
    removeAsMemberOganization: builder.mutation({
      query: (data) => {
        const token = JSON.parse(localStorage.getItem("token"));
        return {
          url: `/jaaga/api/user/organization/app/removeMember`,
          data: data,
          method: "PUT",
          headers: { "x-auth-token": token },
        };
      },
    }),
    addIndividualMember: builder.mutation({
      query: (data) => {
        const token = JSON.parse(localStorage.getItem("token"));
        return {
          url: `/jaaga/api/user/organization/app/addIndividualMember`,
          data: data,
          method: "POST",
          headers: { "x-auth-token": token },
        };
      },
    }),
    addUserAddress: builder.mutation({
      query: (data) => {
        const token = JSON.parse(localStorage.getItem("token"));
        const bodyJson = JSON.stringify(data);
        const hmac = CryptoJS.HmacSHA256(bodyJson, HMAC_SECRET_KEY).toString();
        return {
          url: `/jaaga/api/user/address`,
          data: data,
          method: "POST",
          headers: { "x-auth-token": token, "x-signature": hmac },
        };
      },
    }),
    getAllUserAddresses: builder.query({
      query: () => {
        const token = JSON.parse(localStorage.getItem("token"));
        return {
          url: `/jaaga/api/user/address/getAll`,
          method: "GET",
          headers: { "x-auth-token": token },
        };
      },
    }),
    deleteUserAddress: builder.mutation({
      query: (id) => {
        const token = JSON.parse(localStorage.getItem("token"));

        return {
          url: `/jaaga/api/user/address/${id}`,
          method: "DELETE",
          headers: { "x-auth-token": token },
        };
      },
    }),
    deleteAdminImage: builder.mutation({
      query: ({ propertyId, data }) => {
        const token = JSON.parse(localStorage.getItem("token"));
        // console.log(data);
        return {
          url: `/jaaga/api/user/property-details/app/property/delete/${propertyId}`,
          method: "DELETE",
          data: data,
          headers: { "x-auth-token": token },
        };
      },
    }),
    updateUserAddress: builder.mutation({
      query: ({ data, id }) => {
        // console.log(id);
        const token = JSON.parse(localStorage.getItem("token"));
        const bodyJson = JSON.stringify(data);
        const hmac = CryptoJS.HmacSHA256(bodyJson, HMAC_SECRET_KEY).toString();
        return {
          url: `/jaaga/api/user/address/${id}`,
          method: "PUT",
          data: data,
          headers: { "x-auth-token": token, "x-signature": hmac },
        };
      },
    }),
  }),
});

export const {
  useFetchUserOrdersMutation,
  useFetchCreditHistoryMutation,
  useFetchUserTableCountQuery,
  useFetchUserTransactionsMutation,
  useFetchListOfOrganisationsMutation,
  useFetchListOfPartnerOrganisationsMutation,
  useFetchAdminListOfOrganisationsMutation,
  useGetOneOrganizationsQuery,
  useCreateOrganizationMutation,
  useAssignAdminOrganizationsMutation,
  useDeleteOrganizationMutation,
  useUpdateOrganizationMutation,
  useRemoveAsMemberOganizationMutation,
  useAddIndividualMemberMutation,
  useGetPaymentLinkByOrderIdQuery,
  useFetchUserOrderIdQuery,
  useAddUserAddressMutation,
  useGetAllUserAddressesQuery,
  useDeleteUserAddressMutation,
  useUpdateUserAddressMutation,
  useDeleteAdminImageMutation,
  useUploadOrgTemplateMutation,
  useUploadOrganizationLogoMutation,
  useUpdateOrgDocumentsMutation,
  useGetPartnerOrganizationQuery,
  useFetchLawyerCasesOverviewQuery,
  useFetchLawyerCaseByIdQuery,
  useUploadLawyerCaseDocumentMutation,
} = userApiService;
